// src/Component/Main/Modal/Info/Notification/NotificationModal.js
import React, { useState, useEffect } from 'react';
import api from '../../../../../api';
import styles from './NotificationModal.module.css';

export default function NotificationModal({ visible, onClose }) {
  // 1) SQL 쿼리 입력 상태
  const [sqlQuery, setSqlQuery] = useState(`SELECT id, user_id FROM hotple_user WHERE is_admin = false;`);
  const [loadingQuery, setLoadingQuery] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // 2) 테이블 데이터 + 컬럼
  const [rows, setRows] = useState([]);          // 쿼리 결과
  const [columns, setColumns] = useState([]);    // 동적 컬럼 리스트

  // 3) 선택된 ID들
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // 4) 메시지 폼
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('https://app.hotple.today');
  const [hpDescription, setHpDescription] = useState(null);
  const [hp, setHp] = useState(null);
  const [needTag, setNeedTag] = useState(false);
  const [sendBoth, setSendBoth] = useState(false);
  const [addHp, setAddHp] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // ──── 모달 열릴 때 기본 쿼리 실행 ──────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    runQuery();
  }, [visible]);

  useEffect(()=>{
    setUrl(addHp ? 'https://app.hotple.today/hp' : 'https://app.hotple.today');
  }, [addHp]);

  // ──── 쿼리 실행 함수 ─────────────────────────────────────────────────
    async function runQuery() {
        setLoadingQuery(true);
        setQueryError(null);

        // 숨길 컬럼 리스트
        const hiddenCols = ['user_pw', 'latitude', 'longitude'];

        try {
            const res = await api.post('/admin/users/query', { query: sqlQuery });
            const data = res.data;  // 예: [{ id, user_id, user_pw, latitude, longitude, ... }, ...]

            // 1) 테이블에 사용할 행 설정
            setRows(data);

            // 2) 컬럼 추출: id, user_id는 고정, 그 외 키 중 숨길 컬럼 제외
            if (data.length > 0) {
            const keys = Object.keys(data[0]);
            const base = ['id', 'user_id'];
            const others = keys
                .filter(k => !base.includes(k))           // base 컬럼 제외
                .filter(k => !hiddenCols.includes(k));     // 숨길 컬럼 제외
            setColumns([...base, ...others]);
            } else {
            setColumns(['id', 'user_id']);
            }

            // 3) 자동 전체 선택
            const allIds = data.map(r => r.id);
            setSelectedIds(new Set(allIds));
            setSelectAll(allIds.length > 0);
        } catch (err) {
            setQueryError(err.response?.data?.error || err.message);
        } finally {
            setLoadingQuery(false);
        }
    }

  // ──── 선택 토글 ─────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function handleSelectAll() {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const allIds = rows.map(r => r.id);
      setSelectedIds(new Set(allIds));
      setSelectAll(true);
    }
  }

  // ──── 알림 전송 ───────────────────────────────────────────────────────
  async function handleSend() {
    if(sending || !title || !message) return;
    if (selectedIds.size === 0) {
      alert('선택된 유저가 없습니다.');
      return;
    }
    const confirm = window.confirm(`${selectedIds.size}명에게 알림을 보내시겠습니까?`);

    if(confirm){
      setSending(true);
      setSendError(null);
      try {
        await api.post('/admin/notifications/send', {
          userIds: Array.from(selectedIds),
          title,
          message,
          url,
          needTag,
          sendBoth,
          hp: addHp ? hp : null,
          hpDescription: addHp ? hpDescription : null
        });
        alert('알림 전송 완료');
      } catch (err) {
        setSendError(err.response?.data?.error || err.message);
      } finally {
        setSending(false);
      }
    }
  }

  // ──── 렌더링 제어 ─────────────────────────────────────────────────────
  if (!visible) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      style={{ zIndex: 2000 }}
    >
      <div
        className={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
        <h2 className={styles.heading}>알림 보내기</h2>

        {/* 1) 쿼리 입력 섹션 */}
        <div className={styles.querySection}>
          <textarea
            className={styles.queryBox}
            rows={4}
            value={sqlQuery}
            onChange={e => setSqlQuery(e.target.value)}
          />
          <button
            className={styles.queryButton}
            onClick={runQuery}
            disabled={loadingQuery}
          >
            {loadingQuery ? '조회 중…' : '유저 조회'}
          </button>
        </div>
        {queryError && (
          <div className={styles.error}>{queryError}</div>
        )}

        {/* 2) 유저 리스트 테이블 */}
        <div className={styles.userList}>
          <div className={styles.userListHeader}>
            <label>
              <input
                className={styles.checkbox}
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              /> 전체선택
            </label>
          </div>
          <table className={styles.userTable}>
            <thead>
              <tr>
                <th></th>
                {columns.map(col => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>
                    <input
                      className={styles.checkbox}
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </td>
                  {columns.map(col => (
                    <td key={col}>
                      {Array.isArray(row[col])
                        ? row[col].join(', ')
                        : String(row[col] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 3) 메시지 폼 */}
        <div className={styles.formField}>
          <label className={styles.label}>제목</label>
          <input
            className={styles.input}
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div className={styles.formField}>
          <label className={styles.label}>메시지</label>
          <textarea
            className={styles.textarea}
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
        </div>
        <div className={styles.formField}>
          <label className={styles.label}>URL</label>
          <input
            className={styles.input}
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
          />
        </div>
        <div className={styles.options}>
          <label>
            <input
              className={styles.checkbox}
              type="checkbox"
              checked={needTag}
              onChange={e => setNeedTag(e.target.checked)}
            /> 태그 필요
          </label>
          <label>
            <input
              className={styles.checkbox}
              type="checkbox"
              checked={sendBoth}
              onChange={e => setSendBoth(e.target.checked)}
            /> FCM+인스타그램 모두
          </label>
          <label>
            <input
              className={styles.checkbox}
              type="checkbox"
              checked={addHp}
              onChange={e => setAddHp(e.target.checked)}
            /> HP 지급
          </label>
        </div>
        {addHp &&
          <>
            <div className={styles.formField}>
              <label className={styles.label}>HP 지급</label>
              <input
                className={styles.input}
                type="text"
                value={hp}
                onChange={e => setHp(e.target.value)}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>HP 지급 설명</label>
              <input
                className={styles.input}
                type="text"
                value={hpDescription}
                onChange={e => setHpDescription(e.target.value)}
              />
            </div>
          </>
        }
        {sendError && (
          <div className={styles.error}>{sendError}</div>
        )}
        <button
          className={styles.sendButton}
          onClick={handleSend}
          disabled={sending}
        >
          {sending ? '전송 중…' : '전송하기'}
        </button>
      </div>
    </div>
  );
}
